import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { useState } from 'react';

import {
  CardContainer,
  CardHeaderRow,
  ReportTitle,
  SeeFullReportCTA,
  ViewReportButton,
} from '../../components';

import {
  RiskChip,
  RiskChipLabel,
  RiskDescription,
  RiskExpansion,
  RiskSection,
} from './components';
import {
  EmailFraudScanSection,
  EmailLinkedRecordsSection,
  EmailPotentialOwnersSection,
} from './sections';

import type { StatusSeverity } from '../utils';
import type { EmailReportData } from 'components/DeepSearch/types';

const FRAUD_BANNER_LABEL = 'High fraud risk';
const FRAUD_BANNER_DESCRIPTION =
  "A confirmed data breach, a frequent history of complaints, and recent abuse reports may indicate elevated risk. We'd avoid replying or sharing personal information with this address.";

const RiskChipIcon = ({ severity }: { severity: StatusSeverity }) => {
  const sx = { fontSize: 16 };
  if (severity === 'success') {
    return <CheckCircleIcon sx={sx} />;
  }
  if (severity === 'warning') {
    return <WarningRoundedIcon sx={sx} />;
  }
  return <ErrorIcon sx={sx} />;
};

interface EmailReportPreviewCardProps {
  data: EmailReportData;
  onViewReport?: () => void;
  onSendMessage?: (content: string, displayContent?: string) => void;
}

export const EmailReportPreviewCard = ({
  data,
  onViewReport,
  onSendMessage,
}: EmailReportPreviewCardProps) => {
  const [riskOpen, setRiskOpen] = useState(false);
  const showBanner = data.fraudScore.trim().toLowerCase() === 'high';

  return (
    <CardContainer>
      <CardHeaderRow sx={{ mb: 2 }}>
        <EmailIcon sx={{ fontSize: 24 }} />
        <ReportTitle m={0}>{data.email || 'Email Report'}</ReportTitle>
        {onViewReport && <ViewReportButton onClick={onViewReport} />}
      </CardHeaderRow>

      {showBanner && (
        <RiskSection>
          <RiskChip
            type="button"
            severity="error"
            aria-expanded={riskOpen}
            onClick={() => setRiskOpen((o) => !o)}
          >
            <RiskChipIcon severity="error" />
            <RiskChipLabel variant="body1">{FRAUD_BANNER_LABEL}</RiskChipLabel>
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 16,
                transition: 'transform 0.2s',
                transform: riskOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          </RiskChip>
          <RiskExpansion in={riskOpen}>
            <RiskDescription variant="body1">
              {FRAUD_BANNER_DESCRIPTION}
            </RiskDescription>
          </RiskExpansion>
        </RiskSection>
      )}

      <EmailFraudScanSection data={data} />

      {data.potentialOwner && (
        <EmailPotentialOwnersSection
          owner={data.potentialOwner}
          onSendMessage={onSendMessage}
        />
      )}
      {(data.potentialAddresses.length > 0 ||
        data.potentialEmails.length > 0 ||
        data.potentialPhoneNumbers.length > 0 ||
        data.socialProfiles.length > 0 ||
        data.jobs.length > 0 ||
        data.education.length > 0) && (
        <EmailLinkedRecordsSection data={data} onSendMessage={onSendMessage} />
      )}

      {onViewReport && <SeeFullReportCTA onClick={onViewReport} />}
    </CardContainer>
  );
};
